import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpService } from '../../services/http.service';
import { ToastService } from '../../services/toast.service';
import { environment } from '../../../environments/environment';
import { BulkPricingModelDialogComponent } from '../dialogs/bulk-pricing-model-dialog/bulk-pricing-model-dialog.component';

export interface PricingTier {
  min_amount: number;
  max_amount: number | null;
  discount_percent: number;
}

export interface PricingModel {
  _id?: string;
  tag: string;
  label?: string;
  tiers: PricingTier[];
  priority: number;
  active: boolean;
  stores: string[];
  note?: string;
  updated_at?: Date;
  updated_by?: { _id: string; name: string };
}

@Component({
  selector: 'app-bulk-pricing-models',
  templateUrl: './bulk-pricing-models.component.html',
  styleUrls: ['./bulk-pricing-models.component.scss']
})
export class BulkPricingModelsComponent implements OnInit {
  public models: PricingModel[] = [];
  public loading = false;

  constructor(
    private httpService: HttpService,
    private toastService: ToastService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadModels();
  }

  loadModels(): void {
    this.loading = true;
    this.httpService.get(environment.API_PATH + '/pricing-model').subscribe({
      next: (models: PricingModel[]) => {
        this.models = models;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading pricing models:', error);
        this.toastService.error('Failed to load bulk pricing models');
        this.loading = false;
      }
    });
  }

  createModel(): void {
    this.openDialog({ mode: 'create' });
  }

  editModel(model: PricingModel): void {
    this.openDialog({ mode: 'edit', model });
  }

  toggleModel(model: PricingModel): void {
    this.save({ ...model, active: !model.active },
      `${model.tag} ${model.active ? 'deactivated' : 'activated'}`);
  }

  deleteModel(model: PricingModel): void {
    if (!confirm(`Delete the bulk pricing model for "${model.tag}"? Products with that tag go back to full price in the feed.`)) {
      return;
    }

    this.httpService.delete(environment.API_PATH + `/pricing-model/${model._id}`).subscribe({
      next: () => {
        this.toastService.success(`Deleted ${model.tag}`);
        this.loadModels();
      },
      error: (error) => this.toastService.error(`Failed to delete: ${error.error?.message || 'unknown error'}`)
    });
  }

  // The feed advertises one price per product, so a tier only applies if a
  // single unit already reaches its order amount.
  tierRange(tier: PricingTier): string {
    return tier.max_amount === null || tier.max_amount === undefined
      ? `$${tier.min_amount}+`
      : `$${tier.min_amount} - $${tier.max_amount}`;
  }

  private openDialog(data: any): void {
    const dialogRef = this.dialog.open(BulkPricingModelDialogComponent, {
      width: '760px',
      disableClose: true,
      data
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadModels();
      }
    });
  }

  private save(model: PricingModel, successMessage: string): void {
    this.httpService.post(environment.API_PATH + '/pricing-model', model).subscribe({
      next: (res: any) => {
        this.toastService.success(successMessage);
        (res?.warnings || []).forEach((warning: string) => this.toastService.warning(warning));
        this.loadModels();
      },
      error: (error) => this.toastService.error(`Failed to save: ${error.error?.message || 'unknown error'}`)
    });
  }
}
